import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Container, Form, Stack } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { UserContext } from "../context/UserContext";
import { useFetchData } from "../helpers/FetchDataHelpers";
import MenuSideBar from "../components/MenuSideBar";
import CameraReviewCard, { qaStateOf } from "../components/CameraReviewCard";
import { parseRoles, roleLabel } from "../helpers/cameraRoles";
import LoadingPage from "./Loading";

const isScanned = (camera) => Boolean(camera.mac_address);

const hasOnboardId = (camera) =>
  camera.backend_id !== null && camera.backend_id !== undefined && camera.backend_id >= 0;

// Sort by onboard (backend) id ascending. Cameras without one yet (never
// scanned, or reset) go last, ordered by synthetic id so they stay stable.
const compareByOnboardId = (a, b) => {
  const aHas = hasOnboardId(a);
  const bHas = hasOnboardId(b);
  if (aHas !== bHas) return aHas ? -1 : 1;
  if (aHas && a.backend_id !== b.backend_id) return a.backend_id - b.backend_id;
  return a.synthetic_id - b.synthetic_id;
};

const ALL_ROLES = "";

// Scrollable list of every camera in a deployment with its 3D preview and
// deployment snapshot side by side, so a reviewer can QA-check the whole
// store in one pass instead of opening cameras one by one on the blueprint.
const CamerasReviewPage = () => {
  const [showMenuSideBar, setShowMenuSideBar] = useState(false);
  const [deployments, setDeployments] = useState(null);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [cameras, setCameras] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [includeUnscanned, setIncludeUnscanned] = useState(false);
  const [roleFilter, setRoleFilter] = useState(ALL_ROLES);
  const [busyCameraId, setBusyCameraId] = useState(null);

  const { deploymentId } = useParams();
  const { userenv } = useContext(UserContext);
  const navigate = useNavigate();
  const fetchData = useFetchData();

  useEffect(() => {
    const fetchDeployments = async () => {
      const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}`;
      const { isResponseOK, data, errorMessage } = await fetchData(url);
      if (isResponseOK && data) {
        setDeployments(data);
      } else {
        console.log(errorMessage);
      }
    };
    fetchDeployments();
  }, [userenv, fetchData]);

  useEffect(() => {
    if (!deployments || deployments.length === 0) return;
    if (deploymentId === "0") {
      navigate(`/${deployments[0].deployment_id}/cameras-review`, { replace: true });
      return;
    }
    const deployment = deployments.find(
      (d) => d.deployment_id.toString() === deploymentId,
    );
    if (!deployment) {
      navigate("/not-found");
    } else {
      setSelectedDeployment(deployment);
    }
  }, [deployments, deploymentId, navigate]);

  const fetchCameras = useCallback(async () => {
    if (!selectedDeployment) return;
    const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${selectedDeployment.deployment_id}/camera-details`;
    const { isResponseOK, data, errorMessage } = await fetchData(url);
    if (isResponseOK && data) {
      setCameras(data);
    } else {
      toast.error(`Error fetching cameras: ${errorMessage}`);
    }
  }, [selectedDeployment, userenv, fetchData]);

  useEffect(() => {
    setCameras(null);
    fetchCameras();
  }, [fetchCameras]);

  const onQaCheck = useCallback(
    async (cameraId, qaValue) => {
      setBusyCameraId(cameraId);
      const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${selectedDeployment.deployment_id}/cameras/${cameraId}/qa-check?qa_check_value=${qaValue}`;
      const { isResponseOK, errorMessage } = await fetchData(url, "PATCH");
      if (isResponseOK) {
        setCameras((prev) =>
          prev.map((c) => (c.camera_id === cameraId ? { ...c, qa_check: qaValue } : c)),
        );
      } else {
        toast.error("Error: " + errorMessage);
      }
      setBusyCameraId(null);
    },
    [userenv, selectedDeployment, fetchData],
  );

  const availableRoles = useMemo(() => {
    if (!cameras) return [];
    return [...new Set(cameras.flatMap((c) => parseRoles(c.roles)))].sort();
  }, [cameras]);

  const visibleCameras = useMemo(() => {
    if (!cameras) return [];
    return cameras
      .filter((c) => includeUnscanned || isScanned(c))
      .filter((c) => !onlyPending || qaStateOf(c) === "pending")
      .filter((c) => roleFilter === ALL_ROLES || parseRoles(c.roles).includes(roleFilter))
      .sort(compareByOnboardId);
  }, [cameras, includeUnscanned, onlyPending, roleFilter]);

  const counts = useMemo(() => {
    if (!cameras) return { scanned: 0, checked: 0 };
    const scanned = cameras.filter(isScanned);
    return {
      scanned: scanned.length,
      checked: scanned.filter((c) => qaStateOf(c) !== "pending").length,
    };
  }, [cameras]);

  if (!deployments || !selectedDeployment || !cameras) {
    return <LoadingPage />;
  }

  return (
    <div className="min-vh-100">
      <Container className="py-3" style={{ maxWidth: 1100 }}>
        <Stack
          direction="horizontal"
          gap={3}
          className="mb-3 flex-wrap text-white align-items-center"
        >
          <Button
            variant="light"
            onClick={() => navigate(`/${selectedDeployment.deployment_id}`)}
            aria-label="Back to blueprint"
          >
            <FaArrowLeft />
          </Button>
          <div>
            <h4 className="mb-0">Camera review</h4>
            <small>{selectedDeployment.store_name}</small>
          </div>
          <div className="ms-auto text-end">
            <div className="fw-semibold">
              {counts.checked} / {counts.scanned} checked
            </div>
            <small>{visibleCameras.length} shown</small>
          </div>
          <Button variant="light" onClick={() => setShowMenuSideBar(true)}>
            Menu
          </Button>
        </Stack>

        <Form className="d-flex flex-wrap gap-4 mb-3 text-white align-items-center">
          <Form.Group controlId="role-filter" className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0">Role</Form.Label>
            <Form.Select
              size="sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value={ALL_ROLES}>All roles</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>{roleLabel(role)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Check
            type="switch"
            id="only-pending-switch"
            label="Only cameras pending QA"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
          />
          <Form.Check
            type="switch"
            id="include-unscanned-switch"
            label="Include unscanned cameras"
            checked={includeUnscanned}
            onChange={(e) => setIncludeUnscanned(e.target.checked)}
          />
        </Form>

        {visibleCameras.length === 0 ? (
          <div className="text-white text-center py-5">
            <h5>Nothing to review</h5>
            <small>No cameras match the current filters.</small>
          </div>
        ) : (
          visibleCameras.map((camera) => (
            <CameraReviewCard
              key={camera.camera_id}
              camera={camera}
              deploymentId={selectedDeployment.deployment_id}
              onQaCheck={onQaCheck}
              disabled={busyCameraId !== null}
            />
          ))
        )}
      </Container>

      <MenuSideBar
        isVisible={showMenuSideBar}
        hideSideBar={() => setShowMenuSideBar(false)}
        selectedStoreName={selectedDeployment.store_name}
        selectedDeploymentId={selectedDeployment.deployment_id}
        deployments={deployments}
        view="review"
      />

      <ToastContainer />
    </div>
  );
};

export default CamerasReviewPage;
